import {
  always,
  anyPass,
  applySpec,
  Arity1Fn,
  assoc,
  complement,
  converge,
  curry,
  equals,
  head,
  identity,
  ifElse,
  is,
  isEmpty,
  isNil,
  keys,
  map,
  mergeRight,
  pipe,
  reduce,
  split,
  toPairs
} from 'ramda'

export const isNotEmpty = complement(isEmpty)
export const isTruthy: <T>(a: T) => boolean = pipe(Boolean, equals(true))
export const callOr = (fn: Arity1Fn, v: any) => ifElse(isTruthy, fn, always(v))
export const defaultIfFalsy = <T>(v: T) =>
  ifElse(isTruthy, identity, always<T>(v))
export const mergeSpec = curry(
  <TResult extends object>(spec: object, value: object) =>
    converge(mergeRight, [identity, applySpec(spec)])(value) as TResult
)

export const objectToArrayAs = curry((keyProp, valueProp) =>
  pipe(
    toPairs,
    map(([key, value]) => ({
      [keyProp]: key,
      [valueProp]: value
    }))
  )
)

export const headObj = <O extends object, K extends keyof O>(
  obj: O = {} as O
): O[K] | object => {
  const ks = keys(obj)
  const header = head(ks)
  return (header && obj?.[header]) || {}
}

export const log =
  (msg: string) =>
  (...args: unknown[]) => {
    console.log(msg, ...args) // eslint-disable-line no-console
    return args
  }

export const wrapArray = ifElse(is(Array), identity, a => [a])

export const parseArray = ifElse(is(Array), identity, split(','))

export const isEmptyOrNull = anyPass([isNil, isEmpty])

export const renameKeys = curry((keysMap, obj) =>
  reduce((acc, key) => assoc(keysMap[key] || key, obj[key], acc), {}, keys(obj))
)

// Simple createSelector replacement without using "reselect" library
export function createSelector(...args: any[]) {
  const hasOptions =
    typeof args[args.length - 1] === 'object' &&
    args[args.length - 1] !== null &&
    !Array.isArray(args[args.length - 1]) &&
    args[args.length - 1].memoizeOptions

  const options = hasOptions ? args.pop() : {}
  const resultFunc = args.pop()
  const inputSelectors = args

  const equalityCheck =
    options?.memoizeOptions?.equalityCheck || ((a: any, b: any) => a === b)

  let lastArgs: any[] = []
  let lastResult: any

  return function (state: any) {
    const currentArgs = inputSelectors.map((selector: any) => selector(state))

    // Check if any input has changed
    const hasChanged =
      lastArgs.length === 0 ||
      lastArgs.length !== currentArgs.length ||
      currentArgs.some(
        (arg: any, index: number) => !equalityCheck(arg, lastArgs[index])
      )

    if (hasChanged) {
      lastResult = resultFunc(...currentArgs)
      lastArgs = currentArgs
    }

    return lastResult
  }
}
